import { useEffect, useRef, useState } from 'react'
import { Box, SwipeableDrawer, Typography } from '@mui/material'
import Slider from 'react-slick'
import DamagesOnVehicle from './../DamagesOnVehicle'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { useTranslation } from 'react-i18next'
import LoadableImage from '../LoadableImage'

export default function DamageDetails({
  damageData,
  placeholder,
  defaultPlaceholder,
  damages,
  damageTypes,
  afterChange,
}) {
  const [shouldOpenDrawer, setShouldOpenDrawer] = useState(false)

  const toggleDrawer = () => {
    setShouldOpenDrawer((shouldOpenDrawer) => !shouldOpenDrawer)
  }

  const [textHeight, setTextHeight] = useState(null)
  const textRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTextHeight(textRef.current?.offsetHeight)
  }, [])

  const settings = {
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    infinite: false,
    arrows: false,
  }
  const [t] = useTranslation()
  const placeholderImage = placeholder || defaultPlaceholder

  const closedDrawerHeight = 76
  const currentDamage = damageData
  return (
    <>
      <Box
        height="100%"
        width="100%"
        display="flex"
        alignItems="center"
        overflow="hidden"
        sx={{
          backgroundColor: '#B6b6b6',
          paddingBottom: 6 + textHeight + 'px',
          '& .slick-slider': {
            width: '100%',
            height: '100%',
          },
          '& .slick-list, .slick-track, .slick-slide': {
            height: '100%',
          },
          '& .slick-slide': {
            '& > div': {
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            },
          },
        }}
      >
        <Slider
          sx={{ width: '100%', height: '100%' }}
          {...settings}
          afterChange={afterChange}
        >
          {currentDamage.photoUrls.map((photoURL: string) => {
            return (
              <LoadableImage
                noLoading
                blobId={photoURL}
                alt={t('alt.damage_details')}
                key={photoURL.slice(0, 10)}
              />
            )
          })}
        </Slider>
      </Box>
      {textHeight === null ? (
        <Box width="100%" px={2}>
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.text.darkSecondary,
              letterSpacing: 0,
              wordBreak: 'break-word',
            }}
            component="div"
            ref={textRef}
          >
            {currentDamage.description}
          </Typography>
        </Box>
      ) : (
        <>
          <SwipeableDrawer
            anchor="bottom"
            open={shouldOpenDrawer}
            onClose={toggleDrawer}
            onOpen={toggleDrawer}
            disableDiscovery
            disableSwipeToOpen={false}
            classes={{
              paper: 'scroll-wrapper',
              docked: 'scroll-wrapper',
            }}
            sx={{
              overflow: 'visible',
              pointerEvents: 'none',
              borderTop: 'none',
            }}
            PaperProps={{
              sx: () => ({
                pointerEvents: 'all',
                visibility: 'visible',
                top: `calc(100% - ${204 + textHeight}px)`,
                overflow: 'visible',
                marginLeft: '-1px',
                marginRight: '-1px',
              }),
            }}
            ModalProps={{
              disableScrollLock: true,
              disableEnforceFocus: true,
              disableAutoFocus: true,
              hideBackdrop: true,
            }}
            disableBackdropTransition
            disablePortal
            minFlingVelocity={250}
            hideBackdrop
            swipeAreaWidth={closedDrawerHeight + textHeight}
          >
            <Box
              px={2.5}
              sx={(theme) => ({
                boxShadow: '0px -2px 1px 0px rgba(0,0,0,0.14)',
                borderRadius: theme.spacing(1, 1, 0, 0),
                position: 'absolute',
                top: -(closedDrawerHeight + textHeight),
                visibility: 'visible',
                background: '#fff',
                right: 0,
                left: 0,
              })}
            >
              <Box
                component="span"
                sx={(theme) => ({
                  width: theme.spacing(5),
                  height: theme.spacing(0.5),
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 8,
                  display: 'block',
                  margin: '0 auto',
                  backgroundColor: '#ddd',
                  borderRadius: theme.spacing(0.5),
                })}
              />
              <Box
                display="flex"
                justifyContent="space-between"
                pt={3}
                alignItems="center"
              >
                <Typography
                  variant="h2"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100% - 80px)',
                  }}
                >
                  {Array.isArray(currentDamage.classifications)
                    ? currentDamage.classifications
                        .map(
                          (classification: string | number) =>
                            damageTypes[classification]
                        )
                        .join(', ')
                    : damageTypes[currentDamage.classifications]}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {/* Fix timestamp */}
                  {new Date(currentDamage.timestamp)
                    ?.toLocaleString('es-ES', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                    })
                    ?.split('/')
                    .join('.')}
                </Typography>
              </Box>
              <Box mt={1.75}>
                <Typography
                  variant="body2"
                  sx={{
                    color: (theme) => theme.palette.text.darkSecondary,
                    letterSpacing: 0,
                    wordBreak: 'break-word',
                  }}
                >
                  {currentDamage.description}
                </Typography>
              </Box>
              <DamagesOnVehicle
                damages={damages}
                vehiclePlaceholderImage={placeholderImage}
                highlightedDamage={currentDamage}
              />
            </Box>
          </SwipeableDrawer>
        </>
      )}
    </>
  )
}
